import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { discoveryApi } from '../discovery/discoveryApi';
import { providersApi } from '../providers/providersApi';
import { strategyApi } from '../strategy/strategyApi';
import {
  ALL_STRATEGY_MEMBER_TAGS,
} from '../strategy/constants';
import { MEDRM_TAG_TYPE } from './constants';
import type { RootState } from 'src/redux/reducers';
import type { DiscoveryListRequest } from '../discovery/types';

interface FieldDefinition {
  id: string;
  label: string;
  description: string;
  input_type: string;
  nullable: boolean;
  options: MedScout.Status[];
}

interface ActivityProvider {
  id: string;
  name: string;
  provider_id: string;
  specialty: string;
  type: string;
}

interface ActivityOwner {
  first_name: string;
  last_name: string;
}

interface Activity {
  id: string;
  type: string;
  category: number | null;
  provider: ActivityProvider;
  owner: ActivityOwner;
  created_at: string;
  activity_date: string;
  details: string;
}

interface GetActivitiesParams {
  reps?: string[] | null;
  activityTypes?: string[] | null;
  categories?: string[] | null;
  statuses?: Record<string, string[]> | null;
  timeframe?: string | null;
  sort?: string | null;
  order?: 'asc' | 'desc' | null;
  page?: number;
  pageSize?: number;
  q?: string;
}

interface ObjectDefinition {
  id: string;
  name: string;
  // Add other fields as needed
}

interface PaginatedResponse<T> {
  results: T[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

interface UpdateStatusPayload {
  crmLinkId: string;
  object_id: string;
  fields?: {
    status?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface ActivityType {
  id: number;
  label: string;
  icon: string;
  color: string;
  activity_count?: number;
  deactivated?: boolean;
}

export interface ActivityCategory {
  id: number;
  label: string;
  deactivated?: boolean;
  activity_count?: number;
}

interface CreateActivityCategoryParams {
  data: Partial<ActivityCategory> | Partial<ActivityCategory>[];
}

interface UpdateActivityCategoryParams {
  categoryId: number;
  data: Partial<ActivityCategory>;
}

interface GetEntityActivitiesParams {
  providerId: string;
  activityTypes?: string[];
  categories?: string[];
}

interface CreateActivityParams {
  providerId: string;
  data: {
    type: string | number;
    details: string;
    date?: string;
    category?: number | null;
  };
}

interface UpdateActivityParams {
  activityId: string;
  data: {
    type?: string | number;
    details?: string;
    date?: string;
    category?: number | null;
  };
}

interface CreateActivityTypeParams {
  data: Partial<ActivityType> | Partial<ActivityType>[];
}

interface UpdateActivityTypeParams {
  activityTypeId: number;
  data: Partial<ActivityType>;
}

// Helper to build query string for activities
const buildActivitiesQueryString = ({
  reps,
  activityTypes,
  categories,
  statuses,
  timeframe,
  sort,
  order,
  page,
  pageSize,
  q,
}: GetActivitiesParams): string => {
  const params = new URLSearchParams();

  // Pagination & sorting
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  if (page !== undefined) params.append('page', String(page + 1)); // API is 1-indexed
  if (pageSize) params.append('pageSize', String(pageSize));
  if (q) params.append('q', q);

  // Reps filter
  if (reps?.length) {
    reps.forEach((rep) => params.append('rep', rep));
  }

  // Activity types filter
  if (activityTypes?.length) {
    activityTypes.forEach((type) => params.append('activity_type', type));
  }

  // Categories filter
  if (categories?.length) {
    categories.forEach((cat) => params.append('category', cat));
  }

  // Statuses filter
  if (statuses && Object.keys(statuses).length > 0) {
    let index = 1;
    Object.entries(statuses).forEach(([crmType, statusArray]) => {
      (statusArray as string[]).forEach((crmStatus) => {
        params.append(`crm_type_${index}`, crmType);
        params.append(`crm_status_${index}`, crmStatus);
        index++;
      });
    });
  }

  // Timeframe filter
  if (timeframe) {
    params.append('time_range', timeframe);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

interface DiscoveryResultCRMLink {
  id?: number;
  crm?: string;
  crm_status?: string | null;
}

interface DiscoveryResultItem {
  provider_id: string | number;
  crm_links?: DiscoveryResultCRMLink[];
}

export const medrmApi = createApi({
  reducerPath: 'medrmApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [MEDRM_TAG_TYPE],
  endpoints: (builder) => ({
    // Activities Endpoints
    getActivities: builder.query<
      PaginatedResponse<Activity>,
      GetActivitiesParams
    >({
      query: (params) => ({
        url: `v1/medrm/activity/${buildActivitiesQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITIES' }],
    }),

    getActivityById: builder.query<Activity, { activityId: string }>({
      query: ({ activityId }) => ({
        url: `v1/medrm/activity/${activityId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { activityId }) => [
        { type: MEDRM_TAG_TYPE, id: `ACTIVITY_${activityId}` },
      ],
    }),

    getEntityActivities: builder.query<
      PaginatedResponse<Activity>,
      GetEntityActivitiesParams
    >({
      query: ({ providerId, activityTypes, categories }) => {
        const params = new URLSearchParams();
        if (activityTypes?.length) {
          activityTypes.forEach((type) => params.append('activity_type', type));
        }
        if (categories?.length) {
          categories.forEach((cat) => params.append('category', cat));
        }
        const queryString = params.toString();
        return {
          url: `v1/medrm/activity/entity/${providerId}/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { providerId }) => [
        { type: MEDRM_TAG_TYPE, id: `ENTITY_ACTIVITIES_${providerId}` },
        { type: MEDRM_TAG_TYPE, id: 'ACTIVITIES' },
      ],
    }),

    createActivity: builder.mutation<Activity, CreateActivityParams>({
      query: ({ providerId, data }) => ({
        url: `v1/medrm/activity/entity/${providerId}/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { providerId }) => [
        { type: MEDRM_TAG_TYPE, id: `ENTITY_ACTIVITIES_${providerId}` },
        { type: MEDRM_TAG_TYPE, id: 'ACTIVITIES' },
      ],
    }),

    updateActivity: builder.mutation<Activity, UpdateActivityParams>({
      query: ({ activityId, data }) => ({
        url: `v1/medrm/activity/${activityId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { activityId }) => [
        { type: MEDRM_TAG_TYPE, id: `ACTIVITY_${activityId}` },
        { type: MEDRM_TAG_TYPE, id: 'ACTIVITIES' },
      ],
    }),

    deleteActivity: builder.mutation<void, { activityId: string }>({
      query: ({ activityId }) => ({
        url: `v1/medrm/activity/${activityId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITIES' }],
    }),

    // Activity Types Endpoints
    getActivityTypes: builder.query<PaginatedResponse<ActivityType>, void>({
      query: () => ({
        url: 'v1/medrm/activity-types/',
        method: 'GET',
      }),
      providesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITY_TYPES' }],
    }),

    getActivityType: builder.query<ActivityType, { activityTypeId: number }>({
      query: ({ activityTypeId }) => ({
        url: `v1/medrm/activity-types/${activityTypeId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { activityTypeId }) => [
        { type: MEDRM_TAG_TYPE, id: `ACTIVITY_TYPE_${activityTypeId}` },
      ],
    }),

    createActivityType: builder.mutation<
      ActivityType | ActivityType[],
      CreateActivityTypeParams
    >({
      query: ({ data }) => ({
        url: 'v1/medrm/activity-types/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITY_TYPES' }],
    }),

    updateActivityType: builder.mutation<ActivityType, UpdateActivityTypeParams>(
      {
        query: ({ activityTypeId, data }) => ({
          url: `v1/medrm/activity-types/${activityTypeId}`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (result, error, { activityTypeId }) => [
          { type: MEDRM_TAG_TYPE, id: 'ACTIVITY_TYPES' },
          { type: MEDRM_TAG_TYPE, id: `ACTIVITY_TYPE_${activityTypeId}` },
        ],
      }
    ),

    deleteActivityType: builder.mutation<void, { activityTypeId: number }>({
      query: ({ activityTypeId }) => ({
        url: `v1/medrm/activity-types/${activityTypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITY_TYPES' }],
    }),

    // Activity Categories Endpoints
    getActivityCategories: builder.query<
      PaginatedResponse<ActivityCategory>,
      void
    >({
      query: () => ({
        url: 'v1/medrm/activity-categories/',
        method: 'GET',
      }),
      providesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITY_CATEGORIES' }],
    }),

    createActivityCategory: builder.mutation<
      ActivityCategory | ActivityCategory[],
      CreateActivityCategoryParams
    >({
      query: ({ data }) => ({
        url: 'v1/medrm/activity-categories/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: MEDRM_TAG_TYPE, id: 'ACTIVITY_CATEGORIES' },
      ],
    }),

    updateActivityCategory: builder.mutation<
      ActivityCategory,
      UpdateActivityCategoryParams
    >({
      query: ({ categoryId, data }) => ({
        url: `v1/medrm/activity-categories/${categoryId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [
        { type: MEDRM_TAG_TYPE, id: 'ACTIVITY_CATEGORIES' },
      ],
    }),

    deleteActivityCategory: builder.mutation<void, { categoryId: number }>({
      query: ({ categoryId }) => ({
        url: `v1/medrm/activity-categories/${categoryId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: MEDRM_TAG_TYPE, id: 'ACTIVITY_CATEGORIES' },
      ],
    }),

    // Object Definition Endpoints (Read-only)
    getObjectDefinitionsList: builder.query<
      PaginatedResponse<ObjectDefinition>,
      void
    >({
      query: () => ({
        url: 'v1/medrm/object-definitions/',
        method: 'GET',
      }),
      providesTags: [{ type: MEDRM_TAG_TYPE, id: 'OBJECT_LIST' }],
    }),

    getObjectDefinitionDetails: builder.query<ObjectDefinition, { id: string }>(
      {
        query: ({ id }) => ({
          url: `v1/medrm/object-definitions/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, { id }) => [
          { type: MEDRM_TAG_TYPE, id: `OBJECT_${id}` },
        ],
      }
    ),

    // Field Definition Endpoints (CRUD)
    getFieldDefinitions: builder.query<
      PaginatedResponse<FieldDefinition>,
      { objectId: string }
    >({
      query: ({ objectId }) => ({
        url: `v1/medrm/object-definitions/${objectId}/fields/`,
        method: 'GET',
      }),
      providesTags: (result, error, { objectId }) => [
        { type: MEDRM_TAG_TYPE, id: `FIELDS_${objectId}` },
      ],
    }),

    getFieldDefinitionDetails: builder.query<
      FieldDefinition,
      { id: string; objectId: string }
    >({
      query: ({ id, objectId }) => ({
        url: `v1/medrm/object-definitions/${objectId}/fields/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { id, objectId }) => [
        { type: MEDRM_TAG_TYPE, id: `FIELD_${objectId}_${id}` },
      ],
    }),

    addFieldDefinition: builder.mutation<
      FieldDefinition,
      FieldDefinition & { objectId: string }
    >({
      query: (data) => ({
        url: `v1/medrm/object-definitions/${data.objectId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { objectId }) => [
        { type: MEDRM_TAG_TYPE, id: `FIELDS_${objectId}` },
      ],
    }),

    updateFieldDefinitionDetails: builder.mutation<
      FieldDefinition,
      { data: Partial<FieldDefinition>; objectId: string; id: string }
    >({
      query: ({ data, objectId, id }) => ({
        url: `v1/medrm/object-definitions/${objectId}/fields/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { objectId, id }) => [
        { type: MEDRM_TAG_TYPE, id: `FIELDS_${objectId}` },
        { type: MEDRM_TAG_TYPE, id: `FIELD_${objectId}_${id}` },
      ],
    }),

    deleteFieldDefinition: builder.mutation<
      void,
      { id: string; objectId: string }
    >({
      query: ({ id, objectId }) => ({
        url: `v1/medrm/object-definitions/${objectId}/fields/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { objectId }) => [
        { type: MEDRM_TAG_TYPE, id: `FIELDS_${objectId}` },
      ],
    }),

    // Status Update Endpoint
    updateMedRMStatus: builder.mutation<void, UpdateStatusPayload>({
      query: (data) => ({
        url: `v1/integrations/crm-links/${data.crmLinkId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: MEDRM_TAG_TYPE, id: 'ACTIVITIES' }],
      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        try {
          await queryFulfilled;

          // Patch just the affected provider's crm_status in all active discovery
          // list cache entries instead of invalidating the whole list.
          const state = getState() as RootState;
          const discoveryQueries = state.discoveryApi.queries;
          for (const entry of Object.values(discoveryQueries)) {
            if (entry?.endpointName !== 'getDiscoveryList' || !entry.data) continue;
            dispatch(
              discoveryApi.util.updateQueryData(
                'getDiscoveryList',
                entry.originalArgs as DiscoveryListRequest,
                (draft) => {
                  const results = draft.results as DiscoveryResultItem[];
                  const item = results?.find(
                    (r) => String(r.provider_id) === String(arg.object_id)
                  );
                  const link = item?.crm_links?.find(
                    (l) => String(l.id) === String(arg.crmLinkId)
                  );
                  if (link) {
                    link.crm_status = arg.fields?.status ?? null;
                  }
                }
              )
            );
          }

          // MAP view still needs a refetch — it renders pins, not rows
          dispatch(
            discoveryApi.util.invalidateTags([
              { type: 'Discovery', id: 'MAP' },
            ])
          );

          // Invalidate provider cache for profile pages
          if (arg.object_id) {
            dispatch(
              providersApi.util.invalidateTags([
                { type: 'Provider', providerId: String(arg.object_id) },
              ] as any)
            );
          }

          // Invalidate Strategy Center cache so its provider list/map reflect
          // the updated CRM link status.
          dispatch(
            strategyApi.util.invalidateTags([...ALL_STRATEGY_MEMBER_TAGS])
          );
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),
  }),
});

export const {
  // Activities
  useGetActivitiesQuery,
  useGetActivityByIdQuery,
  useGetEntityActivitiesQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  // Activity Types
  useGetActivityTypesQuery,
  useGetActivityTypeQuery,
  useCreateActivityTypeMutation,
  useUpdateActivityTypeMutation,
  useDeleteActivityTypeMutation,
  // Activity Categories
  useGetActivityCategoriesQuery,
  useCreateActivityCategoryMutation,
  useUpdateActivityCategoryMutation,
  useDeleteActivityCategoryMutation,
  // Object Definitions
  useGetObjectDefinitionsListQuery,
  useGetObjectDefinitionDetailsQuery,
  // Field Definitions
  useGetFieldDefinitionsQuery,
  useGetFieldDefinitionDetailsQuery,
  useAddFieldDefinitionMutation,
  useUpdateFieldDefinitionDetailsMutation,
  useDeleteFieldDefinitionMutation,
  // Status
  useUpdateMedRMStatusMutation,
} = medrmApi;
